/**
 * @file ShipmentEventRawSheet.tsx
 * @description Сырой ответ перевозчика по событию доставки (phase19.13 шаг 6).
 *
 * Нужен при разборе инцидента: «наш этап» — результат маппинга, а спорить с
 * перевозчиком приходится его же payload'ом. Поэтому JSON показываем как есть
 * и даём скопировать одной кнопкой.
 */

import { Copy } from "lucide-react";

import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import {
  DELIVERY_STAGE_LABEL,
  type ShipmentEventDto,
} from "../../lib/api-delivery-monitoring";
import { formatShopDateTime } from "../../lib/shop-time";

interface Props {
  /** Событие для показа; `null` — панель закрыта. */
  event: ShipmentEventDto | null;
  onClose: () => void;
}

export function ShipmentEventRawSheet({ event, onClose }: Props) {
  const notify = useNotify();
  const json = event ? safeStringify(event.raw) : "";

  async function copyJson(): Promise<void> {
    try {
      await navigator.clipboard.writeText(json);
      notify.success("JSON скопирован");
    } catch {
      notify.error("Браузер не дал скопировать — выделите текст вручную");
    }
  }

  return (
    <Sheet open={event !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-none md:max-w-xl">
        {/* shrink-0 обязателен: в flex-col рядом с overflow-y блоком шапка
            схлопывается — видно только на узком экране. */}
        <SheetHeader className="shrink-0">
          <SheetTitle>Ответ перевозчика</SheetTitle>
          <SheetDescription>
            {event
              ? `${DELIVERY_STAGE_LABEL[event.stage]} · ${formatShopDateTime(event.occurredAt)} · ${event.carrierCode}`
              : ""}
          </SheetDescription>
        </SheetHeader>

        <pre className="min-h-0 flex-1 overflow-auto rounded-md bg-surface-low p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all">
          {json}
        </pre>

        <Button
          variant="outline"
          className="min-h-11 shrink-0"
          onClick={() => void copyJson()}
        >
          <Copy className="mr-1.5 size-4" />
          Скопировать
        </Button>
      </SheetContent>
    </Sheet>
  );
}

/** JSON с отступами; циклические структуры не роняют панель. */
function safeStringify(value: unknown): string {
  if (value === undefined || value === null) return "Перевозчик не прислал тело события.";
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}
